/* eslint-disable no-return-assign */
import React, {
  useState, useContext, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Link from 'next/link';

import {
  TextInput, Select, Checkbox, Form, Button, PhoneInput,
} from 'components/common';
import { withTranslation } from 'language/i18n';
import { getObjectByLanguage } from 'language/helpers';
import { createCompany } from 'models/user';
import { StatusContext } from 'contexts/statusContext';
import { maxLengthValidator, minLengthValidator, nameValidator } from 'utils/formValidators';

const CreateCompany = ({
  t, userState, companyState, updateCompanyState, sources, router,
}) => {
  const { setErrorState } = useContext(StatusContext);

  const [onResponse, setOnResponse] = useState(false);
  let formRef = useRef();

  const listElement = useMemo(() => {
    const {
      countries, regions, sectors, taxOffices, learnedFroms, businessTypes, districtByRegion,
    } = sources;

    const countryList = _.map(countries, (country) => {
      return { value: country.id, text: getObjectByLanguage(country.name) };
    });

    const regionList = _.map(regions, (region) => {
      return { value: region.id, text: getObjectByLanguage(region.name) };
    });

    const districtList = _.map(districtByRegion, (district) => {
      return { value: district.id, text: getObjectByLanguage(district.name) };
    });

    const sectorList = _.map(sectors, (sector) => {
      return { value: sector.id, text: getObjectByLanguage(sector.name) };
    });

    const taxOfficeList = _.map(taxOffices, (taxOffice) => {
      return { value: taxOffice.id, text: getObjectByLanguage(taxOffice.name) };
    });

    const learnedFromList = _.map(learnedFroms, (learnedFrom) => {
      return { value: learnedFrom.id, text: getObjectByLanguage(learnedFrom.name) };
    });

    const businessTypeList = _.map(businessTypes, (businessType) => {
      return { value: businessType.id, text: getObjectByLanguage(businessType.name) };
    });

    return {
      countryList,
      regionList,
      districtList,
      sectorList,
      taxOfficeList,
      learnedFromList,
      businessTypeList,
    };
  }, [sources]);

  const onFormSubmit = async () => {
    if (!onResponse) {
      try {
        setOnResponse(true);
        const status = await createCompany({
          userId: userState.id,
          ...companyState,
        });

        if (status === 201) {
          router.push({
            pathname: '/register/register-success',
          });
        }
      } catch (error) {
        console.log('registerErr', error);
        if (error.status) {
          setErrorState({
            title: t('alert'),
            text: error.message,
            buttonText: t('close'),
            onClickButton: () => setErrorState({}),
          });
        }
      } finally {
        setOnResponse(false);
      }
    }
  };

  return (
    <div className="col-md-10 col-lg-7 offset-lg-1 mt-5 mt-md-0">
      <Form
        layout="vertical"
        onFinish={onFormSubmit}
        formRef={(ref) => formRef = ref}
      >
        <div
          className="row mb-2 ml-1"
          style={{
            width: '100%', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <div className="fs-22">{t('stepNumber', { step: 5 })}</div>
          <Link href="/register/register-success">
            <div className="register-skip-button">{t('skip')}</div>
          </Link>
        </div>

        <div className="fs-22 fw-900 mb-5">{t('enterCompanyInformation')}</div>

        <div className="row mb-4">
          <div className="col">
            <TextInput
              name="title"
              placeholder={t('companyNameOffical')}
              value={companyState.title}
              onChange={updateCompanyState}
              required
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <Select
              name="taxOffice"
              placeholder={t('taxOffice')}
              onChange={updateCompanyState}
              value={companyState.taxOffice}
              options={listElement.taxOfficeList}
              required
              showSearch
              filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
            />
          </div>
          <div className="col">
            <TextInput
              type="number"
              id="only-number"
              name="taxNo"
              placeholder={t('taxNumber')}
              maxLength={10}
              value={companyState.taxNo}
              onChange={({ value }) => {
                const fixedInput = value.toLocaleUpperCase().replace(/[^0-9A-Z]/g, '');
                formRef.setFieldsValue({
                  taxNo: fixedInput,
                });
                updateCompanyState({ name: 'taxNo', value: fixedInput });
              }}
              validator={(rule, value) => minLengthValidator(value, 10)}
              required
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <TextInput
              name="address"
              placeholder={t('address')}
              value={companyState.address}
              onChange={updateCompanyState}
              required
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <Select
              name="country"
              placeholder={t('country')}
              onChange={updateCompanyState}
              value={companyState.country}
              options={listElement.countryList}
              showSearch
              filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
              required
            />
          </div>
          <div className="col">
            <Select
              name="region"
              placeholder={t('province')}
              onChange={updateCompanyState}
              value={companyState.region}
              options={listElement.regionList}
              required
              showSearch
              filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
            />
          </div>
        </div>

        <div className="row mb-5">
          <div className="col">
            <Select
              name="district"
              placeholder={t('district')}
              onChange={updateCompanyState}
              value={companyState.district}
              options={listElement.districtList}
              required
              showSearch
              filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
            />
          </div>
          <div className="col">
            <TextInput
              id="only-number"
              type="number"
              name="postalCode"
              placeholder={t('postalCode')}
              value={companyState.postalCode}
              onChange={updateCompanyState}
              required
              validator={(rule, value) => maxLengthValidator(value, 5)}
            />
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-15">
            <div className="fs-18 fw-700 mb-4">{t('companyOffical')}</div>
          </div>
          <div className="col-15">
            <div className="row mb-3">
              <div className="col">
                <TextInput
                  name="emergencyContactName"
                  placeholder={t('name')}
                  value={companyState.emergencyContactName}
                  onChange={updateCompanyState}
                  required
                  validator={nameValidator}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <TextInput
                  name="emergencyContactSurname"
                  placeholder={t('surname')}
                  value={companyState.emergencyContactSurname}
                  onChange={updateCompanyState}
                  required
                  validator={nameValidator}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <TextInput
                  name="emergencyContactTitle"
                  placeholder={t('title')}
                  value={companyState.emergencyContactTitle}
                  onChange={updateCompanyState}
                  required
                  validator={nameValidator}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <PhoneInput
                  name="emergencyContactPhone"
                  label={t('phoneNumber')}
                  value={companyState.emergencyContactPhone}
                  onChange={updateCompanyState}
                  required
                  mobileNumber
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-15">
            <div className="fs-18 fw-700 mb-4">{t('describeYourCompanyAndRole')}</div>
          </div>
          <div className="col-15">
            <div className="row mb-3">
              <div className="col">
                <Select
                  name="sector"
                  placeholder={t('sector')}
                  onChange={updateCompanyState}
                  value={companyState.sector}
                  options={listElement.sectorList}
                  showSearch
                  filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
                  required
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <Select
                  name="businessType"
                  placeholder={t('businessType')}
                  onChange={updateCompanyState}
                  value={companyState.businessType}
                  options={listElement.businessTypeList}
                  required
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <TextInput
                  name="position"
                  placeholder={`${t('position')}/${t('title')}`}
                  value={companyState.position}
                  onChange={updateCompanyState}
                  required
                  validator={nameValidator}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-15">
            <div className="fs-18 fw-700 mb-4">{t('purposeOfVisit')}</div>
          </div>
          <div className="col-15">
            <div className="row mb-3">
              <div className="col">
                <div className="im-box im-box--white mb-2 im-box--shadow im-box--padding-15">
                  <Checkbox
                    name="isBuyer"
                    value={companyState.isBuyer}
                    onChange={(data) => {
                      updateCompanyState(data);
                      formRef.validateFields(['isBuyer', 'isSeller']);
                    }}
                    text={t('buyer')}
                    required={!companyState.isSeller}
                  />
                </div>
              </div>
              <div className="col">
                <div className="im-box im-box--white mb-2 im-box--shadow im-box--padding-15">
                  <Checkbox
                    name="isSeller"
                    value={companyState.isSeller}
                    onChange={(data) => {
                      updateCompanyState(data);
                      formRef.validateFields(['isBuyer', 'isSeller']);
                    }}
                    text={t('seller')}
                    required={!companyState.isBuyer}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-15">
            <div className="fs-18 fw-700 mb-4">{t('whereDidYouHearAboutUs')}</div>
          </div>
          <div className="col-15">
            <div className="row mb-3">
              <div className="col">
                <Select
                  name="learnedFrom"
                  placeholder={t('channel')}
                  onChange={updateCompanyState}
                  value={companyState.learnedFrom}
                  options={listElement.learnedFromList}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-15">
            <div className="fs-18 fw-700 mb-4">{t('machineParkSize')}</div>
          </div>
          <div className="col-15">
            <div className="row">
              <div className="col">
                <Select
                  name="machineParkSize"
                  placeholder={t('numberOfMachines')}
                  onChange={updateCompanyState}
                  value={companyState.machineParkSize}
                  options={[
                    { value: '1', text: '1' },
                    { value: '2', text: '2' },
                    { value: '3', text: '3' },
                    { value: '4', text: '4' },
                    { value: '5', text: '5' },
                    { value: '5-25', text: '5-25' },
                    { value: '25-50', text: '25-50' },
                    { value: '50-100', text: '50-100' },
                    { value: '100-500', text: '100-500' },
                    { value: '500+', text: '500+' },
                    { value: '0', text: 'Makina Parkım Yok' },
                  ]}
                  required
                />
              </div>
            </div>
          </div>
        </div>
        <Button
          loading={onResponse}
          htmlType="submit"
          className="im-button im-button--primary im-button--fluid im-button--center mt-5 mb-5"
          label={t('next')}
        />
      </Form>
    </div>
  );
};

CreateCompany.propTypes = {
  t: PropTypes.func,
  updateCompanyState: PropTypes.func,
  companyState: PropTypes.object,
  userState: PropTypes.object,
  sources: PropTypes.object,
  router: PropTypes.object,
};

export default withTranslation('common')(CreateCompany);
